import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating } from "@/components/StarRating";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { BadgeCheck, Car, Music, PawPrint, Cigarette, MessageCircle, Star, Pencil, Loader2, CalendarDays } from "lucide-react";

const Profile = () => {
  const { userId } = useParams();
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [edit, setEdit] = useState({ name: "", bio: "", carModel: "", carColor: "", music: true, pets: false, smoking: false });

  const load = () => {
    setLoading(true);
    api.get(`/users/${userId}`).then((r) => {
      setData(r.data);
      const u = r.data.user;
      setEdit({ name: u.name || "", bio: u.bio || "", carModel: u.car?.model || "", carColor: u.car?.color || "", music: u.preferences?.music ?? true, pets: u.preferences?.pets ?? false, smoking: u.preferences?.smoking ?? false });
    }).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(load, [userId]);

  const isOwn = user && user.user_id === userId;

  const saveProfile = async () => {
    setSavingEdit(true);
    try {
      await api.put("/profile", { name: edit.name, bio: edit.bio, car: (edit.carModel ? { model: edit.carModel, color: edit.carColor } : null), preferences: { music: edit.music, pets: edit.pets, smoking: edit.smoking, chatty: data.user.preferences?.chatty || "depends" } });
      toast.success("Profile updated");
      setEditOpen(false); refreshUser && refreshUser(); load();
    } catch (e) { toast.error("Could not save profile"); } finally { setSavingEdit(false); }
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { reviewee_id: userId, rating, comment });
      toast.success("Review posted — thanks!");
      setReviewOpen(false); setComment(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post review"); }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-10"><Skeleton className="h-56 w-full bg-white/5 rounded-[var(--radius)]" /></div>;
  if (!data) return <div className="max-w-4xl mx-auto px-4 py-20 text-center font-display text-2xl">User not found</div>;

  const u = data.user; const prefs = u.preferences || {};
  const memberSince = u.created_at ? new Date(u.created_at).getFullYear() : "";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Card className="glass p-6">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          <Avatar className="h-24 w-24 ring-2 ring-[rgba(34,211,238,0.4)]"><AvatarImage src={u.picture} alt={u.name} /><AvatarFallback className="bg-[#3B82F6] text-white text-2xl">{(u.name||"?").slice(0,2)}</AvatarFallback></Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl font-semibold">{u.name}</h1>
              {u.verified_email && <Badge className="bg-[rgba(34,211,238,0.14)] text-[#A5F3FC] border border-[rgba(34,211,238,0.22)] gap-1"><BadgeCheck size={12} /> Verified</Badge>}
            </div>
            <div className="mt-1"><StarRating value={u.rating || 0} count={u.review_count} size={18} /></div>
            {u.bio && <p className="text-secondary-cosmic mt-3 max-w-xl">{u.bio}</p>}
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-cosmic">
              <span className="flex items-center gap-1"><CalendarDays size={14} className="text-[#22D3EE]" /> Member since {memberSince}</span>
              <span>{data.ride_count} rides published</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              {u.car && <span className="flex items-center gap-1 text-secondary-cosmic"><Car size={15} className="text-[#22D3EE]" /> {u.car.model}{u.car.color ? ` · ${u.car.color}` : ""}</span>}
              {prefs.music && <span className="flex items-center gap-1 text-secondary-cosmic"><Music size={15} className="text-[#22D3EE]" /> Music</span>}
              {prefs.pets && <span className="flex items-center gap-1 text-secondary-cosmic"><PawPrint size={15} className="text-[#22D3EE]" /> Pets ok</span>}
              {!prefs.smoking && <span className="flex items-center gap-1 text-secondary-cosmic"><Cigarette size={15} className="text-[#22D3EE]" /> Non-smoking</span>}
            </div>
          </div>
          <div className="flex gap-2">
            {isOwn ? (
              <Dialog open={editOpen} onOpenChange={setEditOpen}><DialogTrigger asChild><Button data-testid="profile-edit-button" variant="outline" className="bg-white/5 border-white/10 text-white hover:bg-white/8 gap-1"><Pencil size={15} /> Edit</Button></DialogTrigger>
                <DialogContent className="glass-solid text-white border-white/10"><DialogHeader><DialogTitle>Edit profile</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="bg-white/5 border-white/10 text-white" /></div>
                    <div><Label>Bio</Label><Textarea value={edit.bio} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} className="bg-white/5 border-white/10 text-white" /></div>
                    <div className="grid grid-cols-2 gap-2"><div><Label>Car model</Label><Input value={edit.carModel} onChange={(e) => setEdit({ ...edit, carModel: e.target.value })} className="bg-white/5 border-white/10 text-white" /></div><div><Label>Car color</Label><Input value={edit.carColor} onChange={(e) => setEdit({ ...edit, carColor: e.target.value })} className="bg-white/5 border-white/10 text-white" /></div></div>
                    <div className="flex items-center justify-between"><span>Music</span><Switch checked={edit.music} onCheckedChange={(v) => setEdit({ ...edit, music: v })} /></div>
                    <div className="flex items-center justify-between"><span>Pets welcome</span><Switch checked={edit.pets} onCheckedChange={(v) => setEdit({ ...edit, pets: v })} /></div>
                    <div className="flex items-center justify-between"><span>Smoking allowed</span><Switch checked={edit.smoking} onCheckedChange={(v) => setEdit({ ...edit, smoking: v })} /></div>
                  </div>
                  <DialogFooter><Button onClick={saveProfile} disabled={savingEdit} className="bg-[hsl(var(--primary))] text-white">{savingEdit ? <Loader2 className="animate-spin" size={16} /> : "Save"}</Button></DialogFooter>
                </DialogContent></Dialog>
            ) : user ? (
              <Dialog open={reviewOpen} onOpenChange={setReviewOpen}><DialogTrigger asChild><Button data-testid="profile-review-button" className="bg-[hsl(var(--primary))] text-white glow-violet hover:glow-cyan gap-1"><Star size={15} /> Leave review</Button></DialogTrigger>
                <DialogContent className="glass-solid text-white border-white/10"><DialogHeader><DialogTitle>Review {u.name}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label className="mb-2 block">Rating</Label><div className="flex gap-1">{[1,2,3,4,5].map((n) => <button key={n} onClick={() => setRating(n)} aria-label={`${n} stars`}><Star size={28} className={n <= rating ? "text-[#F7C948]" : "text-white/20"} fill={n <= rating ? "#F7C948" : "transparent"} /></button>)}</div></div>
                    <div><Label>Comment</Label><Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="How was the ride?" className="bg-white/5 border-white/10 text-white" /></div>
                  </div>
                  <DialogFooter><Button data-testid="profile-review-submit" onClick={submitReview} className="bg-[hsl(var(--primary))] text-white">Post review</Button></DialogFooter>
                </DialogContent></Dialog>
            ) : null}
          </div>
        </div>
      </Card>

      <div className="mt-6">
        <h2 className="font-display text-xl font-semibold mb-4">Reviews ({data.reviews.length})</h2>
        {data.reviews.length === 0 ? (
          <Card className="glass p-8 text-center text-muted-cosmic">No reviews yet.</Card>
        ) : (
          <div className="space-y-3">
            {data.reviews.map((rv) => (
              <Card key={rv.review_id} className="glass p-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9"><AvatarImage src={rv.reviewer?.picture} /><AvatarFallback className="bg-[#3B82F6] text-white text-xs">{(rv.reviewer?.name||"?").slice(0,2)}</AvatarFallback></Avatar>
                  <div><div className="text-white text-sm">{rv.reviewer?.name}</div><StarRating value={rv.rating} size={13} showValue={false} /></div>
                </div>
                {rv.comment && <p className="text-secondary-cosmic text-sm mt-2">{rv.comment}</p>}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
